import {
  createRefund as unboundedCreateRefund,
  getTransaction as unboundedGetTransaction,
  dryRunRefund,
} from 'admin/actions/transactions';
import Tooltip from 'admin/components/atoms/Tooltip';
import ApiResult from 'admin/components/shared/ApiResult';
import React, { useEffect, useState } from 'react';
import CSSModules from 'react-css-modules';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { Button } from '../../atoms/Buttons';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import FA from '../../atoms/FontAwesome';
import TextArea from '../../atoms/TextArea';
import BreadCrumb from '../../molecules/BreadCrumb';
import CancelButton from './CancelButton';
import CombinedPaymentTable from './CombinedPaymentTable';
import DetailTable from './DetailTable';
import HistoryTable from './HistoryTable';
import styles from './styles.scss';
import AdminUser from 'admin/models/admin_user';
import { components } from 'admin/schema/docs/api/admin';
import { TransferWithPointDebt } from 'admin/actions/transactions';
import { formatNum, formatUUID } from 'admin/utils';

type Transaction = components['schemas']['TransactionDetail'];
type CombinedPaymentLeg = components['schemas']['CombinedPaymentTransaction'];

type Props = {
  user: AdminUser;
  match: { params: { id: string } };
  createRefund: (id: string, description: string) => Promise<TransferWithPointDebt>;
  getTransaction: (id: string, privateMoneyId?: string | null) => Promise<Transaction>;
  location: { search: string };
};

const ModalWarningMessage = styled.div`
  display: flex;
  gap: 0.5rem;

  p {
    margin-bottom: 0;
    color: #d75f48;
  }
`;

const Icon = styled(FA)`
  font-size: 16px;
`;

const CancelUnavailableNotice = styled.div`
  ${({ theme }) => theme.typography.body2.regular}
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.yellow[50]};
  border: 1px solid ${({ theme }) => theme.colors.yellow[300]};
  border-radius: ${({ theme }) => theme.borderRadius[4]};
  color: ${({ theme }) => theme.colors.yellow[700]};

  .font-awesome-icon {
    color: ${({ theme }) => theme.colors.yellow[400]};
  }

  p {
    margin-bottom: 0;
  }
`;

const NoteWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: start;

  p {
    margin-bottom: 0;
  }
`;

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
};

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '取引管理', href: '', isCurrentPage: true },
  { value: '取引一覧', href: '/transactions', isCurrentPage: false },
  { value: '取引詳細', href: '', isCurrentPage: true },
];

const initialTransation: Transaction = {
  id: '',
  type: undefined,
  rows: [],
  organization_code: '',
  combined_payment: null,
};

/** 併用払いの内訳のうち、いま見ている取引以外(＝一緒に取り消される取引) */
const siblingLegs = (transaction: Transaction): CombinedPaymentLeg[] =>
  (transaction.combined_payment?.transactions ?? []).filter((leg) => !leg.is_current);

const initialApiResult = {
  message: '',
  isError: false,
};

const TransactionDetail = ({ user, match, createRefund, getTransaction, location }: Props) => {
  const transactionId = match.params.id;
  const privateMoneyId = new URLSearchParams(location.search).get('private_money_id');

  const [transaction, setTransaction] = useState<Transaction>(initialTransation);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState<Array<string>>([]);
  const [refundDescription, setRefundDescription] = useState('');
  const [apiResult, setApiResult] = useState(initialApiResult);

  // 併用払いの内訳から対になる取引へ飛ぶと、同じルートのまま URL だけが変わる。
  // 依存配列が空だと React Router はこのコンポーネントを再マウントしないので、
  // URL は変わったのに前の取引が表示されたままになる。
  // 続けて何度も遷移すると古いリクエストが後から解決しうるので、
  // 用済みになったレスポンスは捨てる(後勝ちで別の取引を表示してしまうため)
  useEffect(() => {
    let stale = false;
    getTransaction(transactionId, privateMoneyId).then((loaded) => {
      if (!stale) setTransaction(loaded);
    });
    return () => {
      stale = true;
    };
  }, [transactionId, privateMoneyId]);

  const flashApiResult = () => setTimeout(() => setApiResult(initialApiResult), 6000);

  const openModal = async () => {
    if (transaction.id !== transactionId) return;
    setIsLoading(true);
    try {
      const dryRun = await dryRunRefund(transactionId);
      const shopNames = dryRun.point_debt?.rows?.map((row) => row.bear_point_shop_name) ?? []
      // 併用払いはグループ単位でしか取り消せない。対になる取引も戻ることを先に言う
      const isCombined = siblingLegs(transaction).length > 0;
      const prefix = isCombined ? 'この取引は併用払いです。対になる取引も同時に取り消されます。' : '';
      if (dryRun.point_debt?.total > 0) {
        setModalTitle(`${prefix}負債が発生しますが、取引の取り消しを実行してもよろしいですか？`);
        setModalDescription(shopNames);
      } else {
        setModalTitle(`${prefix}取引の取り消しを実行してもよろしいですか？`);
        setModalDescription([]);
      }
      setIsModalOpen(true);
    } catch (error) {
      const resolvedError = await Promise.resolve(error);
      setApiResult({
        message:
          typeof resolvedError === 'string'
            ? resolvedError
            : (resolvedError?.message ?? 'エラーが発生しました'),
        isError: true,
      });
      flashApiResult();
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setModalTitle('');
    setModalDescription([]);
    setIsModalOpen(false);
  };

  const onConfirm = () => {
    closeModal();
    createRefund(transactionId, refundDescription)
      .then(() => {
        setApiResult({
          message: '取引のキャンセルに成功しました',
          isError: false,
        });
        getTransaction(transactionId, privateMoneyId).then(setTransaction);
        flashApiResult();
      })
      .catch(async (error) => {
        const resolvedError = await Promise.resolve(error);
        setApiResult({
          message:
            typeof resolvedError === 'string'
              ? resolvedError
              : (resolvedError?.message ?? '取引のキャンセルに失敗しました'),
          isError: true,
        });
        flashApiResult();
      });
  };

  const isExchange =
    transaction.type === 'exchange_inflow' || transaction.type === 'exchange_outflow';

  // 対レグへ遷移すると同じルートのまま URL だけ変わり、再取得が終わるまで前の取引が
  // 画面に残る。その間に取消を押せてしまうと、画面に出ている内容を確認したつもりで
  // URL 側の別の取引を取り消すことになる。取消は元に戻せないので、読み込みが
  // 追いつくまでボタンごと止める
  const isCurrentTransactionLoaded = transaction.id === transactionId;

  return (
    <div>
      <BreadCrumb crumbs={CRUMBS} />
      <ApiResult message={apiResult.message} isError={apiResult.isError} />
      {transaction.payment_method_type === 'paytree-gw' && (
        <CancelUnavailableNotice>
          <Icon icon="exclamation-triangle" />
          <p>コード読取り取引に対してPokepay側での取引キャンセルは行えません</p>
        </CancelUnavailableNotice>
      )}
      <div>
        <div className={styles.buttons}>
          <CancelButton
            disabled={isLoading || !isCurrentTransactionLoaded}
            transaction={transaction}
            onClick={openModal}
            user={user}
          />
        </div>
        <div styleName="page-header">
          <span>取引ID: {transaction.id}</span>
          <CopyToClipboard text={transaction.id ?? ''} tooltipId="transactionId" />
        </div>
        <div styleName="page-header">
          <span>マネー: {transaction.private_money?.name}</span>
        </div>
        <div styleName="page-header">
          <span>Veritrans取引ID: {transaction.veritrans_order_id}</span>
          {transaction.veritrans_order_id && <CopyToClipboard text={transaction.veritrans_order_id} tooltipId="veritransOrderId" />}
        </div>
        <div styleName="page-description">取引説明文: {transaction.description}</div>
        <DetailTable transaction={transaction} user={user} privateMoneyId={privateMoneyId} />
        <CombinedPaymentTable combinedPayment={transaction.combined_payment} />
        {!isExchange && (
          <div styleName="title">
            <span>
              更新履歴
              <Tooltip
                id="helpUpdateHistoryLabel"
                text="支払・チャージやキャンペーン適用、取引キャンセルなどの履歴です"
              />
            </span>
          </div>
        )}
        <HistoryTable transaction={transaction} />
        {/* TODO: waiting to be implemented */}
        {/*
        <div className={styles.headline}>取引情報</div>
        <SubDetailTable transaction={transaction} /> */}
      </div>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <div>
          <p>{modalTitle}</p>
          <ModalWarningMessage>
            <Icon icon="exclamation-triangle" color="#D75F48" />
            <p>この操作は元に戻せません</p>
          </ModalWarningMessage>
        </div>
        {siblingLegs(transaction).length > 0 && (
          <div style={{ alignSelf: 'start', textAlign: 'left', marginTop: '1rem', border: '1px solid #ddd', padding: '10px' }}>
            <p style={{ margin: '0 0 6px 0', fontWeight: 'bold' }}>同時に取り消される取引</p>
            {siblingLegs(transaction).map((leg) => (
              <p key={leg.id} style={{ margin: 0 }}>
                {leg.private_money_name}　¥
                {formatNum((leg.money_amount ?? 0) + (leg.raw_point_amount ?? 0))}　(
                {formatUUID(leg.id)})
              </p>
            ))}
            <p style={{ margin: '6px 0 0 0', borderTop: '1px solid #ddd', paddingTop: '6px', fontWeight: 'bold' }}>
              合計 ¥{formatNum(transaction.combined_payment?.total_amount ?? 0)}
            </p>
          </div>
        )}
        {modalDescription && modalDescription.length > 0 && (
          <div style={{ alignSelf: 'start', textAlign: 'left', marginTop: '1rem' }}>
            <p style={{ margin: 0 }}>ポイント負担先店舗:</p>
            {modalDescription.map((shopName, index) => (
              <p key={index} style={{ margin: 0 }}>
                {shopName}
              </p>
            ))}
          </div>
        )}
        <NoteWrapper>
          <p htmlFor="refund_description">説明</p>
          <TextArea
            fullWidth
            rows="1"
            name="refund_description"
            id="refund_description"
            value={refundDescription}
            onChange={(e) => setRefundDescription(e.target.value)}
          />
          <p style={{ fontSize: '12px', color: '#666' }}>
            対象取引に表示する説明文を入力してください
          </p>
        </NoteWrapper>
        <div styleName="button-group">
          <Button onClick={closeModal} variant="neutral">
            キャンセル
          </Button>
          <Button onClick={onConfirm} variant="danger">
            実行
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { getTransaction: unboundedGetTransaction, createRefund: unboundedCreateRefund },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CSSModules(TransactionDetail, styles));
